import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaMapMarkerAlt, FaUsers } from "react-icons/fa";
import eventPlaceholder from "../assets/event-placeholder.jpg";
import communityImage from "../assets/community.jpg";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/events`);
        const data = await response.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleViewDetails = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isUpcoming = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  };

  const upcomingEvents = events.filter(event => isUpcoming(event.date));
  const pastEvents = events.filter(event => !isUpcoming(event.date));

  return (
    <>
      {/* Hero Section */}
      <section
        className="py-5 d-flex align-items-center position-relative"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${communityImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "60vh",
          marginTop: "-80px",
          paddingTop: "80px"
        }}
      >
        <Container className="text-center text-white">
          <Row className="justify-content-center">
            <Col lg={8}>
              <Badge bg="warning" className="mb-3 fs-6 px-3 py-2">COMMUNITY EVENTS</Badge>
              <h1 className="fw-bold display-4 mb-4">Our Events</h1>
              <p className="lead fs-5 mb-4">
                Stay connected with our community through meaningful gatherings,
                cultural celebrations, and important meetings that bring us together.
              </p>
              <div className="d-flex justify-content-center gap-4 flex-wrap">
                <div className="text-center">
                  <FaCalendarAlt size={24} className="mb-2" />
                  <div className="fw-bold">Quarterly Meetings</div>
                </div>
                <div className="text-center">
                  <FaUsers size={24} className="mb-2" />
                  <div className="fw-bold">Community Gatherings</div>
                </div>
                <div className="text-center">
                  <FaMapMarkerAlt size={24} className="mb-2" />
                  <div className="fw-bold">Texas Locations</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="py-5">
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="warning" size="lg" />
            <p className="mt-3 text-muted">Loading community events...</p>
          </div>
        ) : (
          <>
            {/* Featured/Next Event */}
            {upcomingEvents.length > 0 && (
              <section className="mb-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold section-heading">Next Event</h2>
                  <p className="text-muted">Don't miss our upcoming community gathering</p>
                </div>

                <Row className="justify-content-center">
                  <Col lg={10}>
                    <Card className="border-0 shadow-lg overflow-hidden featured-event-card">
                      <Row className="g-0">
                        <Col md={5}>
                          <img
                            src={upcomingEvents[0].image || eventPlaceholder}
                            alt="Featured Event"
                            className="img-fluid h-100"
                            style={{objectFit: "cover", minHeight: "300px"}}
                          />
                        </Col>
                        <Col md={7}>
                          <Card.Body className="p-4 h-100 d-flex flex-column">
                            <div className="mb-3">
                              <Badge bg="success" className="fs-6 px-3 py-2 mb-3 badge-pulse">
                                FEATURED EVENT
                              </Badge>
                              <h3 className="fw-bold text-dark">{upcomingEvents[0].title}</h3>
                            </div>

                            <div className="mb-3">
                              <div className="d-flex align-items-center mb-2">
                                <FaCalendarAlt className="text-warning me-2" />
                                <span className="fw-bold">{formatDate(upcomingEvents[0].date)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-3">
                                <FaMapMarkerAlt className="text-warning me-2" />
                                <span>{upcomingEvents[0].location}</span>
                              </div>
                            </div>

                            <p className="text-muted flex-grow-1 mb-4">
                              {upcomingEvents[0].description}
                            </p>

                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <FaUsers className="text-muted me-2" />
                                <span className="text-muted">
                                  {upcomingEvents[0].rsvps?.length || 0} people attending
                                </span>
                              </div>
                              <Button
                                variant="warning"
                                size="lg"
                                className="fw-bold px-4"
                                onClick={() => handleViewDetails(upcomingEvents[0]._id)}
                              >
                                RSVP Now
                              </Button>
                            </div>
                          </Card.Body>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              </section>
            )}

            {/* Upcoming Events Grid */}
            {upcomingEvents.length > 1 && (
              <section className="mb-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h3 className="fw-bold section-heading">Other Upcoming Events</h3>
                    <p className="text-muted mb-0">More opportunities to connect with our community</p>
                  </div>
                </div>

                <Row className="g-4">
                  {upcomingEvents.slice(1).map((event) => (
                    <Col lg={6} key={event._id}>
                      <Card className="h-100 shadow-sm border-0 overflow-hidden card-hover">
                        <Row className="g-0 h-100">
                          <Col sm={5}>
                            <img
                              src={event.image || eventPlaceholder}
                              alt="Event"
                              className="img-fluid h-100"
                              style={{objectFit: "cover", minHeight: "200px"}}
                            />
                          </Col>
                          <Col sm={7}>
                            <Card.Body className="p-3 h-100 d-flex flex-column">
                              <Badge bg="primary" className="align-self-start mb-2">Upcoming</Badge>
                              <h6 className="fw-bold mb-2">{event.title}</h6>

                              <div className="mb-2">
                                <div className="d-flex align-items-center mb-1">
                                  <FaCalendarAlt className="text-warning me-1" size={12} />
                                  <small className="fw-bold">
                                    {new Date(event.date).toLocaleDateString("en-US", {
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </small>
                                </div>
                                <div className="d-flex align-items-center">
                                  <FaMapMarkerAlt className="text-warning me-1" size={12} />
                                  <small className="text-muted">{event.location}</small>
                                </div>
                              </div>

                              <p className="text-muted small flex-grow-1 mb-2">
                                {event.description?.substring(0, 100)}...
                              </p>

                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="fw-bold"
                                onClick={() => handleViewDetails(event._id)}
                              >
                                View Details
                              </Button>
                            </Card.Body>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </section>
            )}

            {/* No Upcoming Events */}
            {upcomingEvents.length === 0 && (
              <section className="text-center py-5">
                <Card className="border-0 bg-light p-5">
                  <FaCalendarAlt size={48} className="text-muted mb-3" />
                  <h4 className="fw-bold text-muted">No Upcoming Events</h4>
                  <p className="text-muted">
                    Check back soon for exciting community events and gatherings!<br />
                    Our quarterly meetings are scheduled throughout the year.
                  </p>
                  <Button variant="warning" href="/contact" className="mt-3">
                    Contact Us for Information
                  </Button>
                </Card>
              </section>
            )}

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <section className="mt-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h3 className="fw-bold text-muted">Past Events</h3>
                    <p className="text-muted mb-0">Look back at our recent community gatherings</p>
                  </div>
                </div>

                <Row className="g-4">
                  {pastEvents.slice(0, 6).map((event) => (
                    <Col md={6} lg={4} key={event._id}>
                      <Card className="h-100 shadow-sm border-0 past-event-card card-hover">
                        <Card.Img
                          variant="top"
                          src={event.image || eventPlaceholder}
                          alt="Past Event"
                          style={{ height: "180px", objectFit: "cover" }}
                        />
                        <Card.Body className="p-3">
                          <Badge bg="secondary" className="mb-2">Past Event</Badge>
                          <h6 className="fw-bold">{event.title}</h6>
                          <div className="d-flex align-items-center mb-2">
                            <FaCalendarAlt className="text-muted me-1" size={12} />
                            <small className="text-muted">
                              {new Date(event.date).toLocaleDateString("en-US", {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </small>
                          </div>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <FaUsers className="text-muted me-1" size={12} />
                              <small className="text-muted">
                                {event.rsvps?.length || 0} attended
                              </small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </section>
            )}

            {/* Call to Action */}
            <section className="mt-5">
              <Card className="border-0 text-center p-5 section-gold-gradient">
                <Card.Body className="text-white">
                  <h3 className="fw-bold mb-3">Stay Connected</h3>
                  <p className="fs-5 mb-4">
                    Never miss a community event! Join our mailing list or follow us
                    to get notified about upcoming gatherings and important meetings.
                  </p>
                  <div className="d-flex justify-content-center gap-3 flex-wrap">
                    <Button variant="light" size="lg" href="/contact" className="fw-bold px-4">
                      Contact Us
                    </Button>
                    <Button variant="outline-light" size="lg" href="/about" className="fw-bold px-4 btn-outline-thick">
                      Learn More
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </section>
          </>
        )}
      </Container>
    </>
  );
};

export default Events;
